// #595 Verträge: alle Mietverhältnisse gebündelt (Daten am Mieter: Beginn, Ende, Vertragsart, Miete).
// Reine Funktionen – testbar. Angelegte Verträge (Tabelle vertraege) erscheinen als Vertragsnummer.

import { baueMieterZeilen, basisZeilen, type MieterListenRoh } from '../mieter/liste';
import type { ObjektKopfRoh } from '../immobilien/objektKopf';

export type VertragStatus = 'laufend' | 'beginnt' | 'gekuendigt' | 'beendet';
export type VertragFilter = 'laufend' | 'endet' | 'gekuendigt' | 'beginnt' | 'beendet';
export type ArtFilter = 'alle' | 'unbefristet' | 'befristet' | 'staffel' | 'ohne';
export type VertragArt = Exclude<ArtFilter, 'alle'>;

/** Tage bis Vertragsende, ab denen „endet bald“ gilt */
export const ENDET_BALD_TAGE = 90;

export const STATUS_ANZEIGE: Record<VertragStatus, { label: string; ton: 'ok' | 'info' | 'warn' | 'neutral' }> = {
  laufend: { label: 'Laufend', ton: 'ok' },
  beginnt: { label: 'Beginnt', ton: 'info' },
  gekuendigt: { label: 'Gekündigt', ton: 'warn' },
  beendet: { label: 'Beendet', ton: 'neutral' },
};

export const FILTER_LABEL: Record<VertragFilter, string> = {
  laufend: 'Laufend', endet: 'Endet bald', gekuendigt: 'Gekündigt', beginnt: 'Beginnt', beendet: 'Beendet',
};
export const FILTER_REIHENFOLGE: readonly VertragFilter[] = ['laufend', 'endet', 'gekuendigt', 'beginnt', 'beendet'];

export const ART_LABEL: Record<ArtFilter, string> = {
  alle: 'Alle Arten', unbefristet: 'Unbefristet', befristet: 'Befristet', staffel: 'Staffelmiete', ohne: 'Ohne Angabe',
};
export const ART_REIHENFOLGE: readonly ArtFilter[] = ['alle', 'unbefristet', 'befristet', 'staffel', 'ohne'];

export interface VertragZeile {
  /** Mieter-ID – die Zeile öffnet den Mieter */
  id: string;
  name: string;
  objektId: string | null;
  objektName: string;
  einheit: string;
  art: VertragArt;
  artText: string;
  beginn: string | null;
  ende: string | null;
  kalt: number;
  gesamt: number;
  status: VertragStatus;
  /** Ende liegt in den nächsten ENDET_BALD_TAGE Tagen (und der Vertrag ist nicht beendet) */
  endetBald: boolean;
  /** Vertragsnummer eines angelegten Vertrags (Tabelle vertraege), falls vorhanden */
  vertragsnummer: string | null;
  suchtext: string;
}

/** Vertragsart aus mieter.vertragsart (Freitext aus Import/Formular). */
export function vertragArt(text: string | null | undefined): VertragArt {
  const t = (text ?? '').trim().toLowerCase();
  if (!t) return 'ohne';
  if (t.includes('staffel')) return 'staffel';
  if (t.includes('unbefrist')) return 'unbefristet';
  if (t.includes('befrist')) return 'befristet';
  return 'ohne';
}

export function plusTage(iso: string, tage: number): string {
  const d = new Date(`${iso}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + tage);
  return d.toISOString().slice(0, 10);
}

export interface UebersichtEingabe {
  mieter: readonly (MieterListenRoh & { vertragsart?: string | null })[];
  objekte: readonly ObjektKopfRoh[];
  einheiten: readonly { id: string; bezeichnung: string | null }[];
  /** neuester angelegter Vertrag je Mieter */
  vertragsnummern: ReadonlyMap<string, string>;
  heute: string;
}

export function baueVertragZeilen(e: UebersichtEingabe): VertragZeile[] {
  const artJe = new Map(e.mieter.map(m => [m.id, m.vertragsart ?? null]));
  const grenze = plusTage(e.heute, ENDET_BALD_TAGE);
  // Mietkonto/Portal spielen hier keine Rolle – Status nur aus Beginn, Kündigung und Mieterstatus
  return baueMieterZeilen({ mieter: e.mieter, objekte: e.objekte, einheiten: e.einheiten, portal: new Map(), konten: null, heute: e.heute })
    .map(z => {
      const status: VertragStatus = z.inaktiv ? 'beendet' : z.status === 'einzug' ? 'beginnt' : z.status === 'gekuendigt' ? 'gekuendigt' : 'laufend';
      const artText = (artJe.get(z.id) ?? '').trim();
      const nummer = e.vertragsnummern.get(z.id) ?? null;
      return {
        id: z.id, name: z.name, objektId: z.objektId, objektName: z.objektName, einheit: z.einheit,
        art: vertragArt(artText), artText,
        beginn: z.beginn, ende: z.ende, kalt: z.kalt, gesamt: z.gesamt, status,
        endetBald: status !== 'beendet' && !!z.ende && z.ende >= e.heute && z.ende <= grenze,
        vertragsnummer: nummer,
        suchtext: `${z.suchtext} ${(nummer ?? '').toLowerCase()}`,
      };
    });
}

export function passtFilter(z: Pick<VertragZeile, 'status' | 'endetBald'>, f: VertragFilter): boolean {
  switch (f) {
    case 'laufend': return z.status !== 'beendet';
    case 'endet': return z.endetBald;
    case 'gekuendigt': return z.status === 'gekuendigt';
    case 'beginnt': return z.status === 'beginnt';
    case 'beendet': return z.status === 'beendet';
  }
}

export const passtArt = (z: Pick<VertragZeile, 'art'>, a: ArtFilter): boolean => a === 'alle' || z.art === a;

/** Basis für Zähler: Objekt + Suche + Art (der Status-Filter zählt darüber). */
export function basisVertraege(zeilen: readonly VertragZeile[], objekt: string, suche: string, art: ArtFilter): VertragZeile[] {
  return basisZeilen(zeilen, objekt, suche).filter(z => passtArt(z, art));
}

export function zaehle(basis: readonly Pick<VertragZeile, 'status' | 'endetBald'>[]): Record<VertragFilter, number> {
  const n: Record<VertragFilter, number> = { laufend: 0, endet: 0, gekuendigt: 0, beginnt: 0, beendet: 0 };
  for (const z of basis) for (const f of FILTER_REIHENFOLGE) if (passtFilter(z, f)) n[f] += 1;
  return n;
}

const nachOrt = (a: VertragZeile, b: VertragZeile) =>
  a.objektName.localeCompare(b.objektName, 'de', { numeric: true }) || a.einheit.localeCompare(b.einheit, 'de', { numeric: true }) || a.name.localeCompare(b.name, 'de');

/** Sinnvolle Reihenfolge je Filter: bald endende/gekündigte nach Ende, beginnende nach Beginn, beendete neueste zuerst. */
export function sortiere(zeilen: readonly VertragZeile[], f: VertragFilter): VertragZeile[] {
  const liste = [...zeilen];
  if (f === 'endet' || f === 'gekuendigt') return liste.sort((a, b) => (a.ende ?? '9999').localeCompare(b.ende ?? '9999') || nachOrt(a, b));
  if (f === 'beginnt') return liste.sort((a, b) => (a.beginn ?? '').localeCompare(b.beginn ?? '') || nachOrt(a, b));
  if (f === 'beendet') return liste.sort((a, b) => (b.ende ?? '').localeCompare(a.ende ?? '') || nachOrt(a, b));
  return liste.sort(nachOrt);
}

export interface VertragKennzahlen { laufend: number; sollmiete: number; endetBald: number; befristet: number }

export function kennzahlen(zeilen: readonly VertragZeile[]): VertragKennzahlen {
  const laufend = zeilen.filter(z => z.status === 'laufend' || z.status === 'gekuendigt');
  return {
    laufend: laufend.length,
    sollmiete: Math.round(laufend.reduce((s, z) => s + z.gesamt, 0) * 100) / 100,
    endetBald: zeilen.filter(z => z.endetBald).length,
    befristet: laufend.filter(z => z.art === 'befristet').length,
  };
}

export function istVertragFilter(v: string | null): v is VertragFilter {
  return v === 'laufend' || v === 'endet' || v === 'gekuendigt' || v === 'beginnt' || v === 'beendet';
}
export function istArtFilter(v: string | null): v is ArtFilter {
  return v === 'alle' || v === 'unbefristet' || v === 'befristet' || v === 'staffel' || v === 'ohne';
}

/** Neuester angelegter Vertrag je Mieter (Tabelle vertraege). */
export function vertragsnummernJeMieter(vertraege: readonly { mieter_id: string | null; vertragsnummer: string | null; created_at: string | null }[]): Map<string, string> {
  const m = new Map<string, { nr: string; am: string }>();
  for (const v of vertraege) {
    if (!v.mieter_id || !v.vertragsnummer) continue;
    const am = v.created_at ?? '';
    const alt = m.get(v.mieter_id);
    if (!alt || am > alt.am) m.set(v.mieter_id, { nr: v.vertragsnummer, am });
  }
  return new Map([...m].map(([k, v]) => [k, v.nr]));
}
